<template>
  <div v-if="profile" :class="$style.container" :style="`background-image: url(${profile.background});`">
    <div
      :class="$style.list"
    >
      <div :class="$style.top">
        <div :class="$style.topLeft">
          <div :class="$style.pictureWrap">
            <div :class="$style.pictureCircle">
              <img
                :src="profile.picture"
                :alt="`${profile.firstName} ${profile.lastName} profile picture`"
                :class="$style.picture"
              >
            </div>
            <div :class="$style.followersCount">{{ profile.subscribers }}</div>
          </div>
          <template v-if="!profile.isOwn">
            <BaseButton
              v-if="profile.type === 'LOOKING_FOR' && profile.subscribeStatus == null"
              oval dark
              :class="$style.followButton"
              @click="onFollow"
            >
              {{ $t('socialProfile.btnFollow') }}
            </BaseButton>
            <BaseButton
              v-if="profile.type === 'LOOKING_FOR' && (profile.subscribeStatus === 3 || profile.subscribeStatus === 2)"
              oval dark
              :class="$style.followButton"
              @click="onUnfollow"
            >
              {{ $t('socialProfile.btnCancelRequest') }}
            </BaseButton>
            <BaseButton
              v-else-if="profile.type !== 'LOOKING_FOR' || profile.subscribeStatus === 1"
              oval dark
              :class="$style.followButton"
              @click="onUnfollow"
            >
              {{ $t('socialProfile.btnUnfollow') }}
            </BaseButton>
          </template>
        </div>
        <div :class="$style.topRight">
          <div>{{ `${profile.firstName} ${profile.lastName}` }}</div>
          <div>({{ profile.category }})</div>
          <div :class="$style.nickname">/{{ profile.nickname }}</div>
          <Fab
            v-if="!profile.isOwn && profile.isChatActive"
            :class="$style.chatButton"
            @click.native="goChat"
          >
            <BaseIcon name="comment" />
          </Fab>
        </div>
      </div>
      <template v-if="isShowPostList">
        <SocialPostListItem
          v-for="post in posts" :key="post.id"
          :post="post"
          :lastPostsByDay="lastPostsByDay"
          :class="$style.post"
        />
        <infinite-loading
          :identifier="infiniteId"
          @infinite="infiniteHandler"
          :class="$style.infiniteLoading"
        >
          <Spinner slot="spinner" />
          <div slot="no-more"></div>
          <div slot="no-results"></div>
        </infinite-loading>
      </template>
    </div>
    <router-link :to="{ name: 'social-new-post', params: { profileNickname } }">
      <Fab :class="[$style.createButton]">
        <BaseIcon name="plus-circle" />
      </Fab>
    </router-link>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import InfiniteLoading from 'vue-infinite-loading'
import Spinner from '@/components/Spinner'
import SocialPostListItem from './SocialPostListItem'
import Fab from '@/components/Fab'
import { startOfDay } from 'date-fns'
import { TYPE_CODE } from '@/models/Chat'

const OFFSET = 20

export default {
  name: 'social-post-list',
  components: {
    InfiniteLoading,
    Spinner,
    SocialPostListItem,
    Fab
  },
  props: {
    profileNickname: String
  },
  data () {
    return {
      infiniteId: +new Date(),
      page: 0
    }
  },
  // created () {
  //   this.fetchProfile(this.profileNickname)
  // },
  computed: {
    ...mapGetters({
      profileById: 'entities/socialProfiles/getProfile',
      postsByProfile: 'entities/socialPosts/postsByProfile'
    }),
    profile () {
      return this.profileById(this.profileNickname)
    },
    posts () {
      return this.postsByProfile(this.profileNickname)
    },
    lastPostsByDay () {
      return this.posts.reduce((res, post) => {
        const dayBeginTimestamp = startOfDay(new Date(post.createdAt)) * 1
        const savedPost = res[dayBeginTimestamp]
        if (!savedPost || +new Date(post.createdAt) > +new Date(savedPost.createdAt)) {
          res[dayBeginTimestamp] = post
        }
        return res
      }, {})
    },
    isShowPostList () {
      const { type, subscribeStatus } = this.profile
      return type !== 'LOOKING_FOR' || (subscribeStatus !== null && subscribeStatus !== 3)
    }
  },
  watch: {
    'profileNickname': {
      immediate: true,
      handler (nickname) {
        this.page = 0
        this.infiniteId += 1
        this.fetchProfile(nickname)
      }
    }
  },
  methods: {
    ...mapActions({
      fetchProfile: 'entities/socialProfiles/fetchProfile',
      fetchProfilePosts: 'entities/socialPosts/fetchProfilePosts',
      subscribe: 'entities/socialProfiles/subscribe',
      unsubscribe: 'entities/socialProfiles/unsubscribe',
      createChat: 'entities/chats/create'
    }),
    async infiniteHandler ($state) {
      const posts = await this.fetchProfilePosts({
        nickname: this.profileNickname,
        limit: OFFSET,
        offset: OFFSET * this.page
      })
      if (posts.length < OFFSET) {
        $state.complete()
      } else {
        $state.loaded()
        this.page += 1
      }
    },
    onFollow () {
      this.subscribe(this.profileNickname)
    },
    onUnfollow () {
      this.unsubscribe(this.profileNickname)
    },
    async goChat () {
      const { id } = await this.createChat({
        type: TYPE_CODE['one-to-one'],
        participants: [this.profile.owner]
      })
      this.$router.push({ name: 'app-chat', query: { id: window.btoa(id) } })
    }
  }
}
</script>

<style module lang="scss">
.container {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background-repeat: no-repeat;
  background-size: cover;
  flex-grow: 1;
}
.top {
  display: flex;
  justify-content: center;
  // align-items: center;
  background-color: rgba($main-bg-colored, 0.7);
  height: 200px;
  padding-top: 32px;
  // padding: 24px 0 ;
}
.topLeft {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-basis: 50%;
}
.topRight {
  padding-top: 20px;
  color: $text-lightest;
  font-size: 18px;
  flex-basis: 50%;
  position: relative
}
.nickname {
  font-size: 14px;
  font-weight: 300;
  margin-top: 10px;
}
.pictureWrap {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 12px;
}
.pictureCircle {
  border-radius: 50%;
  overflow: hidden;
  width: 100%;
  height: 100%;
}
.picture {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.followersCount {
  border-radius: 8px;
  color: $text-colored;
  font-weight: 600;
  background-color: $main-bg-light;
  text-align: center;
  position: absolute;
  right: 0px;
  bottom: 0px;
  padding: 4px 8px;
}
.chatButton {
  position: absolute;
  bottom: 20px;
  right: 20px;
}
.list {
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: scroll;
}
.infiniteLoading {
  height: 50px;
  padding: 20px 0;
}
.createButton {
  position: absolute;
  bottom: 15px;
  right: 15px;
  font-size: 22px;
  width: 40px;
}
</style>
